import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ArrowLeftIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  BellIcon,
  ServerIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const VoiceSettings = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [carrierStatus, setCarrierStatus] = useState({ connected: false });

  // Settings state - User/Business level settings only
  const [settings, setSettings] = useState({
    // Voice preferences
    voice: {
      defaultCodecs: ['G711U', 'G711A', 'G729', 'OPUS'],
      defaultDtmfMode: 'rfc2833',
      maxConcurrentCalls: 10,
      enableCnam: true,
      enableCallRecording: false,
      recordingStorageDays: 30,
      callerIdName: '',
      defaultCallerIdNumber: ''
    },
    // Security settings
    security: {
      requireTls: true,
      requireSrtp: false,
      ipWhitelistEnabled: false,
      allowedIps: []
    },
    // Notification settings
    notifications: {
      callFailureAlert: true,
      missedCallEmail: true,
      voicemailEmail: true,
      alertEmail: ''
    },
    // Webhook settings (for integrations)
    webhooks: {
      callEvents: '',
      voicemailEvents: ''
    }
  });

  // New IP for whitelist
  const [newIp, setNewIp] = useState('');

  useEffect(() => {
    fetchSettings();
    checkCarrierStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/sip/settings');
      if (response.data.success && response.data.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data.data
        }));
      }
    } catch (error) {
      logger.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCarrierStatus = async () => {
    try {
      const response = await axiosInstance.get('/api/sip/telnyx/status');
      setCarrierStatus({
        connected: response.data.data?.connected || false,
        carrier: response.data.data?.carrier || 'Telnyx'
      });
    } catch (error) {
      logger.error('Error checking carrier status:', error);
      setCarrierStatus({ connected: false, carrier: 'Telnyx' });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.put('/api/sip/settings', settings);
      if (response.data.success) {
        showNotification?.('Settings saved successfully', 'success');
      }
    } catch (error) {
      logger.error('Error saving settings:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save settings';
      showNotification?.(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddIp = () => {
    if (!newIp) return;

    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    if (!ipRegex.test(newIp)) {
      showNotification?.('Please enter a valid IP address or CIDR', 'error');
      return;
    }

    if (!settings.security.allowedIps.includes(newIp)) {
      setSettings({
        ...settings,
        security: {
          ...settings.security,
          allowedIps: [...settings.security.allowedIps, newIp]
        }
      });
    }
    setNewIp('');
  };

  const handleRemoveIp = (ip) => {
    setSettings({
      ...settings,
      security: {
        ...settings.security,
        allowedIps: settings.security.allowedIps.filter(i => i !== ip)
      }
    });
  };

  const toggleCodec = (codec) => {
    const codecs = settings.voice.defaultCodecs;
    if (codecs.includes(codec)) {
      setSettings({
        ...settings,
        voice: {
          ...settings.voice,
          defaultCodecs: codecs.filter(c => c !== codec)
        }
      });
    } else {
      setSettings({
        ...settings,
        voice: {
          ...settings.voice,
          defaultCodecs: [...codecs, codec]
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="h-full bg-white dark:bg-[#111111] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-[#111111] overflow-y-auto">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/voice')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Voice Settings
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Configure your voice and SIP preferences
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Carrier Status Badge */}
              <div className="flex items-center gap-2">
                {carrierStatus.connected ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                    <CheckCircleIcon className="w-4 h-4" />
                    Carrier Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
                    <ExclamationCircleIcon className="w-4 h-4" />
                    Checking Carrier...
                  </span>
                )}
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Caller ID Settings */}
        <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg">
          <div className="p-5 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Cog6ToothIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  Caller ID Settings
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Configure your outbound caller identification
                </p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Caller ID Name
                </label>
                <input
                  type="text"
                  value={settings.voice.callerIdName}
                  onChange={(e) => setSettings({
                    ...settings,
                    voice: { ...settings.voice, callerIdName: e.target.value }
                  })}
                  placeholder="Your Business Name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default Caller ID Number
                </label>
                <input
                  type="text"
                  value={settings.voice.defaultCallerIdNumber}
                  onChange={(e) => setSettings({
                    ...settings,
                    voice: { ...settings.voice, defaultCallerIdNumber: e.target.value }
                  })}
                  placeholder="+1234567890"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500">Must be a verified number from your DIDs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Voice Preferences */}
        <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg">
          <div className="p-5 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Cog6ToothIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  Voice Preferences
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Default settings for your voice services
                </p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Preferred Codecs
              </label>
              <div className="flex flex-wrap gap-2">
                {['G711U', 'G711A', 'G729', 'OPUS', 'G722'].map((codec) => (
                  <button
                    key={codec}
                    onClick={() => toggleCodec(codec)}
                    className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                      settings.voice.defaultCodecs.includes(codec)
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#2a2a2a]'
                    }`}
                  >
                    {codec}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">Select codecs in order of preference</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  DTMF Mode
                </label>
                <select
                  value={settings.voice.defaultDtmfMode}
                  onChange={(e) => setSettings({
                    ...settings,
                    voice: { ...settings.voice, defaultDtmfMode: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                >
                  <option value="rfc2833">RFC 2833 (Recommended)</option>
                  <option value="inband">In-Band</option>
                  <option value="info">SIP INFO</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Concurrent Calls
                </label>
                <input
                  type="number"
                  value={settings.voice.maxConcurrentCalls}
                  onChange={(e) => setSettings({
                    ...settings,
                    voice: { ...settings.voice, maxConcurrentCalls: parseInt(e.target.value) || 1 }
                  })}
                  min="1"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.voice.enableCnam}
                  onChange={(e) => setSettings({
                    ...settings,
                    voice: { ...settings.voice, enableCnam: e.target.checked }
                  })}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Enable CNAM lookup for inbound calls
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.voice.enableCallRecording}
                  onChange={(e) => setSettings({
                    ...settings,
                    voice: { ...settings.voice, enableCallRecording: e.target.checked }
                  })}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Enable call recording
                </span>
              </label>
            </div>

            {settings.voice.enableCallRecording && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Recording Storage (Days)
                </label>
                <input
                  type="number"
                  value={settings.voice.recordingStorageDays}
                  onChange={(e) => setSettings({
                    ...settings,
                    voice: { ...settings.voice, recordingStorageDays: parseInt(e.target.value) || 30 }
                  })}
                  min="1"
                  max="365"
                  className="w-48 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>
            )}
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg">
          <div className="p-5 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <ShieldCheckIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  Security
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Encryption and access control settings
                </p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.security.requireTls}
                  onChange={(e) => setSettings({
                    ...settings,
                    security: { ...settings.security, requireTls: e.target.checked }
                  })}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Require TLS for SIP signaling (Recommended)
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.security.requireSrtp}
                  onChange={(e) => setSettings({
                    ...settings,
                    security: { ...settings.security, requireSrtp: e.target.checked }
                  })}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Require SRTP for media encryption
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.security.ipWhitelistEnabled}
                  onChange={(e) => setSettings({
                    ...settings,
                    security: { ...settings.security, ipWhitelistEnabled: e.target.checked }
                  })}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Enable IP whitelist for SIP registration
                </span>
              </label>
            </div>

            {settings.security.ipWhitelistEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Allowed IP Addresses
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newIp}
                    onChange={(e) => setNewIp(e.target.value)}
                    placeholder="192.168.1.0/24 or single IP"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                  />
                  <button
                    onClick={handleAddIp}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {settings.security.allowedIps.map((ip) => (
                    <span
                      key={ip}
                      className="inline-flex items-center gap-1 px-2 py-1 text-sm font-mono bg-gray-100 dark:bg-[#222222] rounded"
                    >
                      {ip}
                      <button
                        onClick={() => handleRemoveIp(ip)}
                        className="text-gray-500 hover:text-red-500"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg">
          <div className="p-5 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <BellIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  Notifications
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Email alerts and notifications
                </p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Alert Email
              </label>
              <input
                type="email"
                value={settings.notifications.alertEmail}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, alertEmail: e.target.value }
                })}
                placeholder="admin@company.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.notifications.missedCallEmail}
                  onChange={(e) => setSettings({
                    ...settings,
                    notifications: { ...settings.notifications, missedCallEmail: e.target.checked }
                  })}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Email notification for missed calls
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.notifications.voicemailEmail}
                  onChange={(e) => setSettings({
                    ...settings,
                    notifications: { ...settings.notifications, voicemailEmail: e.target.checked }
                  })}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Email notification for new voicemails
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.notifications.callFailureAlert}
                  onChange={(e) => setSettings({
                    ...settings,
                    notifications: { ...settings.notifications, callFailureAlert: e.target.checked }
                  })}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Alert on repeated call failures
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Webhook Settings */}
        <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg">
          <div className="p-5 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <ServerIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  Webhooks
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Integrate call events with your systems
                </p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Call Events Webhook
              </label>
              <input
                type="url"
                value={settings.webhooks.callEvents}
                onChange={(e) => setSettings({
                  ...settings,
                  webhooks: { ...settings.webhooks, callEvents: e.target.value }
                })}
                placeholder="https://your-server.com/webhooks/calls"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500">Receive call start, end, and status events</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Voicemail Events Webhook
              </label>
              <input
                type="url"
                value={settings.webhooks.voicemailEvents}
                onChange={(e) => setSettings({
                  ...settings,
                  webhooks: { ...settings.webhooks, voicemailEvents: e.target.value }
                })}
                placeholder="https://your-server.com/webhooks/voicemail"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500">Receive new voicemail notifications</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceSettings;
